/**
 * BLU-1506 "Order and wait": the Order Queue's Waiting on blank reads.
 *
 *   useWaitingOnBlankCount  the count on the queue's amber button: one
 *                           `getWaitingOnBlank` call with limit 1, so the
 *                           number is the server's total for the very set the
 *                           view lists (one SQL layer filters, counts, pages).
 *   useWaitingOnBlankList   one page of the view, searched on the server.
 *
 * The procedure never refuses a queue reader: a caller who does not run the
 * floor gets `isOwner: false` and no rows. A 3PL client at a shared floor
 * never runs it, so the page does not ask at all there (`enabled`).
 * Arrived and Send invalidate `getWaitingOnBlank` (every input), so both
 * reads refresh together.
 */
import { ORDER_AND_WAIT_MAX_SEARCH } from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { QUERY_CONFIG } from './useOrderQueue';

/** Orders per page in the view. */
export const WAITING_PAGE_SIZE = 50;

const STALE_MS = 30_000;

export function useWaitingOnBlankCount(locationId: string | undefined, enabled: boolean) {
  const query = trpc.productionOrders.getWaitingOnBlank.useQuery(
    { locationId: locationId ?? '', limit: 1, offset: 0 },
    // Same as the queue's own counts (useOrderQueueCounts): long stale time, no refetch on focus.
    { enabled: enabled && !!locationId, staleTime: QUERY_CONFIG.COUNTS_STALE_TIME, refetchOnWindowFocus: false },
  );
  const isOwner = query.data?.isOwner === true;
  return {
    /** Orders waiting at the floor; 0 for anyone who does not run it. */
    count: isOwner ? (query.data?.total ?? 0) : 0,
    isOwner,
    /** The server has answered at least once (owner or not). */
    answered: query.data !== undefined,
  };
}

export function useWaitingOnBlankList(locationId: string | undefined, search: string, page: number) {
  const trimmed = search.trim().slice(0, ORDER_AND_WAIT_MAX_SEARCH);
  const query = trpc.productionOrders.getWaitingOnBlank.useQuery(
    {
      locationId: locationId ?? '',
      search: trimmed || undefined,
      limit: WAITING_PAGE_SIZE,
      offset: page * WAITING_PAGE_SIZE,
    },
    {
      enabled: !!locationId,
      staleTime: STALE_MS,
      // Keep the last page on screen while the next search or page loads.
      placeholderData: (previous) => previous,
    },
  );
  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error,
    refetch: () => void query.refetch(),
  };
}
